import React from 'react';
import { observer } from 'mobx-react-lite';
import dayjs from 'dayjs';
import {
    FormattedMessage,
    useIntl,
    defineMessages,
} from 'react-intl';

import {
    CalculationLineBlock,
    CalculationPanel,
    ComparisonTable,
    useForm,
    FormComputed,
    Highchart,
    FormComputedAsync,
    fetchHistoricalData,
    Formats,
} from '@ratehub/base-ui';
import { getPaymentFrequencyAndDivisor, SCENARIO_TYPES } from '@ratehub/mtg-common';
import {
    can_amortization_table,
    can_mortgage_payment,
} from '@ratehub/mortgage-calculations';


function CalculatorStepInterestRateRisk() {
    const form = useForm();
    const intl = useIntl();

    // Highchart requires a name for the series, so we use the following message
    // to provide it rather than <FormattedMessage /> which isn't supported at the moment.
    const chartName = intl.formatMessage({
        id: 'mtg-calculators.CalculatorStepInterestRateRisk.bestRates.label',
        defaultMessage: 'Best 5yr fixed rates',
    });

    // Compute lowest rate in Canada on record outside of Form state for use below
    const hasLowestRateLimit = form.values.selectedScenario?.selectedRate?.value - 2.00 < LOWEST_RATE_IN_CANADA_ON_RECORD;

    // Update comparison table line label if selected rate minus 2% is lower than 1.39%
    // this is a requirement by the Product team to update the label if the user selected/input rate minus 2% is lower than 1.39%
    const comparisonTableLines = COMPARISON_TABLE_LINES.map(line => {
        if (line.id === 'selectedRateMinus2' && hasLowestRateLimit) {
            return {
                ...line,
                label: (
                    <FormattedMessage
                        id="mtg-calculators.CalculatorStepInterestRateRisk.lowestRate.label"
                        defaultMessage="Lowest 5-year fixed rate in Canada on record"
                    />
                ),
            };
        }
        return line;
    });

    return (
        <>
            <FormComputed
                name="selectedScenario"
                value={({ scenarios, selectedScenarioNumber }) => {
                    return scenarios[selectedScenarioNumber - 1];
                }}
                dependencies={[ 'scenarios', 'selectedScenarioNumber' ]}
            />

            <FormComputed
                name="amortizationScheduleForTerm"
                value={({ selectedScenario, scenarioType, mortgageAmount }) => {
                    const {
                        totalMortgage,
                        amortization,
                        paymentFrequency,
                        selectedRate,
                    } = selectedScenario || {};

                    const mortgageLoan = scenarioType === SCENARIO_TYPES.RENEWAL ? mortgageAmount : totalMortgage;

                    const { value: mortgageRate, term } = selectedRate || {};

                    const rateTermInYears = term / 12;
                    const { frequency, divisor } = paymentFrequency
                        ? getPaymentFrequencyAndDivisor(paymentFrequency)
                        : {};

                    return can_amortization_table(mortgageLoan, (mortgageRate / 100), amortization, frequency, divisor)
                        ?.slice(0, rateTermInYears);
                }}
                dependencies={[ 'selectedScenario' ]}
            />

            <FormComputed
                name="selectedRateMinus2"
                value={({ selectedScenario }) => {
                    const rateValue = selectedScenario.selectedRate?.value - 2.00;
                    return (rateValue < LOWEST_RATE_IN_CANADA_ON_RECORD ? LOWEST_RATE_IN_CANADA_ON_RECORD : rateValue) / 100;
                }}
                dependencies={[ 'selectedScenario' ]}
            />

            <FormComputed
                name="selectedRatePlus2"
                value={({ selectedScenario }) => (selectedScenario.selectedRate?.value + 2.00) / 100}
                dependencies={[ 'selectedScenario' ]}
            />

            <FormComputed
                name="selectedRatePlus5"
                value={({ selectedScenario }) => (selectedScenario.selectedRate?.value + 5.00) / 100}
                dependencies={[ 'selectedScenario' ]}
            />

            <div className="rh-ml-1_5">
                <div className="rh-mt-0">
                    <CalculationPanel
                        title={(
                            <FormattedMessage
                                id="mtg-calculators.CalculatorStepInterestRateRisk.title"
                                defaultMessage="Your total mortgage amount"
                            />
                        )}
                    >
                        <CalculationLineBlock
                            formulas={FORMULAS}
                        />
                    </CalculationPanel>
                </div>

                <div className="rh-mt-2">
                    <p>
                        <FormattedMessage
                            id="mtg-calculators.CalculatorStepInterestRateRisk.interestRatePayment.introduction"
                            defaultMessage="Using this amount, below we can calculate the corresponding mortgage payments at a variety of interest rates:"
                        />
                    </p>
                    <ComparisonTable
                        headings={COMPARISON_TABLE_HEADINGS}
                        lines={comparisonTableLines}
                        noResultLine
                        variant="uniform"
                        data-test-name="comparison-table"
                    />
                </div>

                <div className="rh-mt-2">
                    <p>
                        <FormattedMessage
                            id="mtg-calculators.CalculatorStepInterestRateRisk.rateHistory.introduction"
                            defaultMessage="Below is a graph that displays the approximate values of competitive 5-year fixed mortgage rates since 2006."
                        />
                    </p>
                    <FormComputedAsync
                        name="chartSeries"
                        getFormValues={formValuesToUse}
                        onlyWhen={onlyWhenValues}
                        onFetchValue={() => onFetchChartSeries({
                            intl,
                            chartName,
                        })}
                        hidden
                    />

                    <Highchart
                        series={form.values.chartSeries}
                        yAxis={{
                            labels: {
                                formatter: function() {
                                    // eslint-disable-next-line react/no-this-in-sfc
                                    return intl.formatNumber(this.value / 100, Formats.number.percent);
                                },
                            },
                        }}
                    />
                </div>
            </div>
        </>
    );
}

function formValuesToUse({ askingPrice, mortgageAmount, chartSeries }) {
    // askingPrice is used within Payment Calc and mortgageAmount is used by Renewal Calc
    return { askingPrice, mortgageAmount, chartSeries };
}

function onlyWhenValues({ askingPrice, mortgageAmount, chartSeries }){
    // The Interest Rate Risk accordion is disabled until either askingPrice (for Payment) or
    // mortgageAmount (for Renewal) is entered. There isn't a need to fetch the chart data until then
    return (Boolean(askingPrice) || Boolean(mortgageAmount)) && chartSeries === undefined;
}

async function onFetchChartSeries({ intl, chartName }) {
    let fetchedData;

    try {
        fetchedData = await fetchHistoricalData({
            series: [ 'best-rates.5y-fixed' ],
            xStart: '2006-01-01',
            lang: intl?.locale?.substring(0, 2),
        });
    } catch (error) {
        return null;
    }

    const seriesDates = fetchedData?.x?.data;

    return Object.keys(fetchedData?.series).reduce((seriesCollection, slug) => {
        const series = fetchedData?.series[slug].data;

        return [
            ...seriesCollection,
            // Create series for the selected column
            {
                name: chartName,
                data: series.reduce((seriesData, yValue, index) => {
                    return [ ...seriesData, [ dayjs(seriesDates[index]).valueOf(), yValue ] ];
                }, []),
            },
            // Create series for min, mean, and max values of selected column
            ...(Object.keys(SERIES_LABEL_MESSAGES).map(key => {
                const statName = intl.formatMessage(SERIES_LABEL_MESSAGES[key]);

                return {
                    name: `${chartName} (${statName})`,
                    data: getStatSeriesData({ series, seriesDates, statSlug: key }),
                    states: {
                        inactive: {
                            opacity: 1,
                        },
                    },
                    stickyTracking: false,
                    zIndex: 0,
                };

            })),
        ];
    }, []);
}

function getStatSeriesData({ series, seriesDates, statSlug }) {
    let statValue = null;

    switch (statSlug) {
        case 'min':
            statValue = Math.min(...series);
            break;
        case 'max':
            statValue = Math.max(...series);
            break;
        case 'mean':
            statValue = series.reduce((sum, val) => sum + val, 0) / series.length;
            break;
    }

    return seriesDates.reduce((seriesData, xValue) => {
        return [ ...seriesData, [ dayjs(xValue).valueOf(), statValue ] ];
    }, []);
}

// calculate the future monthly mortgage payments after a user's selected term (5yrs by default)
function getFutureMonthlyMortgagePayment({ selectedScenario, amortizationScheduleForTerm }, customRate = undefined) {
    // 1) from the amortization schedule, get the mortgage amount at the end of the term
    const mortgageAmount = amortizationScheduleForTerm?.[amortizationScheduleForTerm.length - 1]?.balance;
    // 2) use the custom rate or get the rate from the selected scenario
    const rate = customRate ?? selectedScenario?.selectedRate?.value / 100;
    // 3) calculate the new amortization period by subtracting the rate term from the amortization period
    const amortization = selectedScenario?.amortization - (selectedScenario?.selectedRate?.term / 12);
    // 4) get the payment frequency and divisor
    const { frequency, divisor } = selectedScenario?.paymentFrequency
        ? getPaymentFrequencyAndDivisor(selectedScenario?.paymentFrequency)
        : {};

    return can_mortgage_payment(
        mortgageAmount,
        rate,
        amortization,
        frequency,
        divisor,
    ) ?? null;
}

const LOWEST_RATE_IN_CANADA_ON_RECORD = 1.39;

const COMPARISON_TABLE_HEADINGS = {
    label: undefined,
    columnTwo: (
        <FormattedMessage
            id="mtg-calculators.CalculatorStepInterestRateRisk.interestRate.label"
            defaultMessage="Interest rate"
        />
    ),
    columnThree: (
        <FormattedMessage
            id="mtg-calculators.CalculatorStepInterestRateRisk.payment.label"
            defaultMessage="Payment"
        />
    ),
};

// ids have been added to these objects to be used when modifying it where `comparisonTableLines` is defined.
// they serve no purpose within the ComparisonTable component
const COMPARISON_TABLE_LINES = [
    {
        id: 'selectedRate',
        label: (
            <FormattedMessage
                id="mtg-calculators.CalculatorStepInterestRateRisk.selectedRate.label"
                defaultMessage="Selected Rate"
            />
        ),
        columnOne: {
            value: ({ selectedScenario }) => selectedScenario?.selectedRate?.value / 100,
            format: 'percent',
        },
        columnTwo: {
            value: getFutureMonthlyMortgagePayment,
            format: 'CAD0',
        },
    },
    {
        id: 'selectedRateMinus2',
        label: (
            <FormattedMessage
                id="mtg-calculators.CalculatorStepInterestRateRisk.selectedRateMinus2.label"
                defaultMessage="Selected Rate -2%"
            />
        ),
        columnOne: {
            value: 'selectedRateMinus2',
            format: 'percent',
        },
        columnTwo: {
            value: ({ selectedScenario, amortizationScheduleForTerm, selectedRateMinus2 }) =>
                getFutureMonthlyMortgagePayment({ selectedScenario, amortizationScheduleForTerm }, selectedRateMinus2),
            format: 'CAD0',
        },
    },
    {
        id: 'selectedRatePlus2',
        label: (
            <FormattedMessage
                id="mtg-calculators.CalculatorStepInterestRateRisk.selectedRatePlus2.label"
                defaultMessage="Selected Rate +2%"
            />
        ),
        columnOne: {
            value: 'selectedRatePlus2',
            format: 'percent',
        },
        columnTwo: {
            value: ({ selectedScenario, amortizationScheduleForTerm, selectedRatePlus2 }) =>
                getFutureMonthlyMortgagePayment({ selectedScenario, amortizationScheduleForTerm }, selectedRatePlus2),
            format: 'CAD0',
        },
    },
    {
        id: 'selectedRatePlus5',
        label: (
            <FormattedMessage
                id="mtg-calculators.CalculatorStepInterestRateRisk.selectedRatePlus5.label"
                defaultMessage="Selected Rate +5%"
            />
        ),
        columnOne: {
            value: 'selectedRatePlus5',
            format: 'percent',
        },
        columnTwo: {
            value: ({ selectedScenario, amortizationScheduleForTerm, selectedRatePlus5 }) =>
                getFutureMonthlyMortgagePayment({ selectedScenario, amortizationScheduleForTerm }, selectedRatePlus5),
            format: 'CAD0',
        },
    },
];

const FORMULAS = [
    {
        id: 'mortgageAmountTodayCalculation',
        name: 'mortgageAmountTodayCalculation',
        label: (
            <FormattedMessage
                id="mtg-calculators.CalculatorStepInterestRateRisk.mortgageAmountToday.label"
                defaultMessage="Mortgage amount today"
            />
        ),
        operandValue: ({ selectedScenario, scenarioType, mortgageAmount }) =>
            scenarioType === SCENARIO_TYPES.RENEWAL
                ? mortgageAmount
                : selectedScenario?.totalMortgage,
        operandFormat: 'CAD0',
    },
    {
        id: 'principalOverTermCalculation',
        name: 'principalOverTermCalculation',
        label: (
            <FormattedMessage
                id="mtg-calculators.CalculatorStepInterestRateRisk.principalOverTerm.label"
                defaultMessage="Principal paid off over term"
            />
        ),
        operator: 'minus',
        // a negative value is used to subtract the principal paid off over the term from the mortgage amount today
        operandValue: ({ amortizationScheduleForTerm }) => amortizationScheduleForTerm?.reduce((acc, { principal }) => acc + principal, 0),
        operandFormat: 'CAD0',
    },
    {
        id: 'remainingMortgageCalculation',
        name: 'remainingMortgageCalculation',
        label: (
            <FormattedMessage
                id="mtg-calculators.CalculatorStepInterestRateRisk.remainingMortgage.label"
                defaultMessage="Mortgage remaining at end of term"
            />
        ),
        operator: 'equals',
        // calculate remaining mortgages by adding the mortgage amount today and the principal paid off over the term
        operandValue: ({ selectedScenario, principalOverTermCalculation, scenarioType, mortgageAmount }) =>
            scenarioType === SCENARIO_TYPES.RENEWAL
                ? mortgageAmount - principalOverTermCalculation
                : selectedScenario?.totalMortgage - principalOverTermCalculation,
        operandFormat: 'CAD0',
        result: true,
    },
];

const MESSAGES = defineMessages({
    SERIES_LABEL_MINIMUM: {
        id: 'calculators.interestRateRisk.SeriesLabelMinimum',
        defaultMessage: 'Min.',
    },
    SERIES_LABEL_MEAN: {
        id: 'calculators.interestRateRisk.SeriesLabelMean',
        defaultMessage: 'Avg.',
    },
    SERIES_LABEL_MAXIMUM: {
        id: 'calculators.interestRateRisk.SeriesLabelMaximum',
        defaultMessage: 'Max.',
    },
});

const SERIES_LABEL_MESSAGES = {
    min: MESSAGES.SERIES_LABEL_MINIMUM,
    mean: MESSAGES.SERIES_LABEL_MEAN,
    max: MESSAGES.SERIES_LABEL_MAXIMUM,
};

export default observer(CalculatorStepInterestRateRisk);
